import * as React from "react";
import { useEffect, useState } from "react";
import { useCurrentConversation } from "@/lib/hooks/conversations/use-current-conversation";
import {
  MentorshipFeedback,
  SuggestedTopic,
  MentorshipDetails,
  useRefreshSuggestedTopics,
} from "@/lib/api/mentorships";
import { User } from "@/lib/api/user";
import { UserInfoHeader } from "./user-info-header";
import { SidebarUserInfoSections } from "./sidebar-user-info-sections";
import { SurveyThresholds } from "../index";
import { TooltipTrigger } from "@/components/ui/tooltip";
import { TooltipContent } from "@/components/ui/tooltip";
import { Tooltip } from "@/components/ui/tooltip";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { Tab, TabSwitch } from "./tab-switch";
import { CustomTabs, CustomTabsType } from "../custom-tabs";
import { Note } from "@/lib/api/notes";
import { Session } from "@/lib/api/sessions";
import { WhatToWorkOn } from "./what-to-work-on";
import { TeenWallTab } from "../teen-wall-tab";
import { useTeenWallEnabled } from "@/lib/hooks/use-teen-wall-enabled";

interface DetailedUserInfoProps {
  mentee: User;
  feedback: MentorshipFeedback | null;
  isRematched: boolean;
  surveyThresholds: SurveyThresholds;
  suggestedTopics: SuggestedTopic[];
  mentorship: MentorshipDetails;
  summary: string;
  filteredTabs: Tab[];
  notes: Note[];
  sessions: Session[];
  adminMode: boolean;
  supervisor: User | null;
}

export const DetailedUserInfo = React.forwardRef<
  HTMLDivElement,
  DetailedUserInfoProps
>(
  (
    {
      mentee,
      isRematched,
      suggestedTopics,
      mentorship,
      summary,
      filteredTabs,
      notes,
      sessions,
      adminMode,
      supervisor,
    },
    ref,
  ) => {
    const pendingResourcesTab = useCurrentConversation(
      (state) => state.pendingResourcesTab,
    );
    const teenWallEnabled = useTeenWallEnabled();
    const [currentTab, setCurrentTab] = useState<Tab>("resources");

    useEffect(() => {
      if (pendingResourcesTab) {
        setCurrentTab("resources");
      }
    }, [pendingResourcesTab]);

    const metadata = mentee.profile?.metadata;
    const interests = mentee.profile?.interests;

    const refreshSuggestedTopics = useRefreshSuggestedTopics(mentorship.id);
    return (
      <div ref={ref}>
        <UserInfoHeader
          name={mentee.name}
          sex={mentee.profile?.sex}
          pronouns={mentee.pronouns}
          birthday={mentee.birthday}
          reMatched={isRematched}
          userId={mentee.id}
          redactedFields={mentee.redacted_fields}
          profileRedactedFields={mentee.profile?.redacted_fields}
        />
        <TabSwitch currentTab={currentTab} setCurrentTab={setCurrentTab} />
        {currentTab === "profile" && (
          <>
            <SidebarUserInfoSections
              title="Summary"
              content={summary || "No summary for this teen yet"}
              lastUpdated={new Date()}
              showToggle={false}
            />
            <WhatToWorkOn mentorshipId={mentorship.id} />
            <SidebarUserInfoSections
              title="Interests"
              content={Object.values(metadata?.interestDetails || {}).join(
                ", ",
              )}
              labels={interests || []}
            />
            <SidebarUserInfoSections
              title="Documentation to mentor"
              content={metadata?.mentorUnderstanding || "No teen answer"}
            />
            <SidebarUserInfoSections
              title="Struggles"
              content={
                metadata?.priorityConcerns?.explanations
                  ? Object.values(metadata.priorityConcerns.explanations).join(
                      ". ",
                    )
                  : metadata?.priorityConcerns?.explanations
              }
              labels={
                metadata?.priorityConcerns?.concerns ||
                metadata?.mentorPreferences?.helpWith
              }
            />
            <SidebarUserInfoSections
              title="Proud moments"
              content={metadata?.proudMoment || "No teen answer"}
            />
            <SidebarUserInfoSections
              title="Their vibe"
              labels={metadata?.personalityTraits || []}
            />
            <SidebarUserInfoSections
              title="Closest person"
              labels={metadata?.supportSystem || []}
            />
            <SidebarUserInfoSections
              title="Suggested Topics"
              data={suggestedTopics}
              mentorship={mentorship}
              onRefresh={refreshSuggestedTopics.mutateAsync}
              isRefreshing={refreshSuggestedTopics.isPending}
              type="suggestedTopics"
              tags={[
                <TooltipProvider key="beta-tag">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Badge
                        variant="secondary"
                        className="cursor-pointer bg-blue-100 text-blue-800 hover:bg-blue-200"
                      >
                        Beta
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="w-64">
                        This feature is currently in beta, please carefully
                        monitor the responses before sending to your teen!
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>,
              ]}
            />
            {teenWallEnabled && (
              <div className="mt-4 flex flex-col gap-2">
                <p className="text-sm font-medium text-black">Teen profile</p>
                <TeenWallTab mentorship={mentorship} />
              </div>
            )}
          </>
        )}
        {currentTab === "resources" && (
          <>
            <CustomTabs
              tabs={filteredTabs as unknown as CustomTabsType[]}
              notes={notes || []}
              sessions={sessions || []}
              mentorship={mentorship}
              adminMode={adminMode || false}
              supervisor={supervisor || null}
            />
          </>
        )}
      </div>
    );
  },
);

DetailedUserInfo.displayName = "DetailedUserInfo";
